import { useEffect, useRef, useState } from 'react';

const THRESHOLD = 70;   // px the user must pull before a refresh fires
const MAX_PULL = 110;   // visual cap on the pull distance

/**
 * Lightweight pull-to-refresh for the scores screens (works in the Capacitor
 * iOS/Android WebView and on touch web). Attach the returned `containerRef` to
 * the scrollable content; when the user is scrolled to the top and pulls down
 * past the threshold, `onRefresh` is awaited.
 *
 * Returns { containerRef, pullY, refreshing } so the caller can render a
 * spinner that grows/spins as the user pulls.
 */
export const usePullToRefresh = (onRefresh) => {
  const containerRef = useRef(null);
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const s = useRef({ startY: 0, pulling: false, dist: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const getScroller = (node) => {
      let n = node;
      while (n && n !== document.body) {
        const oy = getComputedStyle(n).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
        n = n.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };
    const scroller = getScroller(el);
    const atTop = () => (scroller.scrollTop || 0) <= 0;

    const onStart = (e) => {
      if (refreshing || !atTop()) { s.current.pulling = false; return; }
      s.current.startY = e.touches[0].clientY;
      s.current.pulling = true;
      s.current.dist = 0;
    };
    const onMove = (e) => {
      if (!s.current.pulling || refreshing) return;
      if (!atTop()) { s.current.pulling = false; s.current.dist = 0; setPullY(0); return; }
      const dy = e.touches[0].clientY - s.current.startY;
      if (dy <= 0) { s.current.dist = 0; setPullY(0); return; }
      const d = Math.min(MAX_PULL, dy * 0.5);  // resistance
      s.current.dist = d;
      setPullY(d);
      if (dy > 6 && e.cancelable) e.preventDefault();  // suppress native bounce
    };
    const onEnd = async () => {
      if (!s.current.pulling) return;
      s.current.pulling = false;
      const triggered = s.current.dist >= THRESHOLD;
      s.current.dist = 0;
      if (triggered && !refreshing) {
        setRefreshing(true);
        setPullY(THRESHOLD);
        try { await onRefresh(); } catch { /* ignore */ }
        setRefreshing(false);
        setPullY(0);
      } else {
        setPullY(0);
      }
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh, refreshing]);

  return { containerRef, pullY, refreshing };
};
